/**
 * The JSON Schema for the published contract — **built from the module's own
 * constants**, not hand-written beside them.
 *
 * A hand-written schema is a second copy of the field vocabulary, the enum
 * members, and the numeric ranges. Copies drift, and a drifted schema is worse
 * than none: it tells a consumer their document is invalid when it is fine, or
 * blesses one that this application will refuse. So the enums come from
 * `DEFAULT_FIELD_ORDER`, `APPLICATIONS`, `IMAGE_FITS`, `DESCRIPTION_STYLES`, and
 * the bounds come from the same `CTA_*_RANGE` and `MAX_*` constants the reducer
 * and the storage parser clamp against.
 *
 * `product-card.v1.schema.json` is the emitted artefact for consumers that
 * cannot import TypeScript (the backend, other services). It is generated from
 * this file and `__tests__/contract.test.ts` fails if the two disagree, so the
 * checked-in copy is never stale.
 *
 * Draft 2020-12.
 */

import {
  APPLICATIONS,
  DEFAULT_CTA_CONFIGURATION,
  DEFAULT_DESCRIPTION_STYLE,
  DEFAULT_IMAGE_FIT,
  CTA_HEIGHT_PX_RANGE,
  CTA_WIDTH_PCT_RANGE,
  DEFAULT_FIELD_ORDER,
  DESCRIPTION_STYLES,
  IMAGE_FITS,
  MAX_CARD_NAME_LENGTH,
  MAX_CTA_LABEL_LENGTH,
  MAX_CUSTOM_CSS_LENGTH,
  MAX_DISPLAY_LABEL_LENGTH,
  PHOTO_ANCHORS,
  REGIONS,
} from '../constants';
import {
  PRODUCT_CARD_COLLECTION_KIND,
  PRODUCT_CARD_SCHEMA_ID,
  PRODUCT_CARD_SCHEMA_VERSION,
} from './version';

/** A rowKey is a pairing token, not prose; the storage parser caps it at 80. */
const MAX_ROW_KEY_LENGTH = 80;

const FIELD_IDS = [...DEFAULT_FIELD_ORDER];
const REGION_IDS = REGIONS.map((region) => region.id);
const ANCHORS = [...PHOTO_ANCHORS.map((anchor) => anchor.id), 'sticky'];
const APPLICATION_IDS = APPLICATIONS.map((application) => application.id);

export const buildProductCardSchema = (): Record<string, unknown> => ({
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  $id: PRODUCT_CARD_SCHEMA_ID,
  title: 'Compatio Product Card collection',
  description:
    'A collection of product-card display templates. A card describes WHICH product ' +
    'fields a renderer shows, in what order, and in which of three regions — it carries ' +
    'no product data of its own. See version.ts for the compatibility policy: within a ' +
    'major version only optional additions and relaxed constraints are permitted, and a ' +
    'reader that sees a higher schemaVersion than it understands must refuse the document.',
  type: 'object',
  // Deliberately NO additionalProperties:false anywhere in this schema: within a
  // major version the contract may gain optional keys, and a consumer validating
  // with a vendored copy of this file must not reject a legally newer document.
  // Readers validate what the schema states and IGNORE unknown keys; the WRITER
  // is held to exact keys by this repo's own tests (a strict twin of this schema
  // with additionalProperties:false injected), not by the published artefact.
  required: ['schemaVersion', 'kind', 'cards'],
  properties: {
    $schema: { type: 'string' },
    schemaVersion: { const: PRODUCT_CARD_SCHEMA_VERSION },
    kind: { const: PRODUCT_CARD_COLLECTION_KIND },
    generatedAt: { type: 'string', format: 'date-time' },
    cards: { type: 'array', items: { $ref: '#/$defs/productCard' } },
  },

  $defs: {
    fieldId: {
      description: 'One of the product fields a card can display.',
      enum: FIELD_IDS,
    },

    productCard: {
      type: 'object',
      required: [
        'schemaVersion',
        'id',
        'name',
        'orientation',
        'footerEnabled',
        'fieldOrder',
        'components',
        'references',
        'updatedAt',
      ],
      properties: {
        schemaVersion: { const: PRODUCT_CARD_SCHEMA_VERSION },
        id: { type: 'string', minLength: 1 },
        name: { type: 'string', maxLength: MAX_CARD_NAME_LENGTH },
        orientation: { enum: ['vertical', 'horizontal'] },
        footerEnabled: {
          type: 'boolean',
          description: 'When false the card has no footer strip and no floating placement.',
        },
        fieldOrder: {
          description:
            'Every known field id exactly once — the editing rail\'s order, including ' +
            'disabled fields (re-enabling one restores its position). Renderers never ' +
            'need it: components carry their own order.',
          type: 'array',
          items: { $ref: '#/$defs/fieldId' },
          uniqueItems: true,
          minItems: FIELD_IDS.length,
          maxItems: FIELD_IDS.length,
        },
        components: {
          description:
            'One self-contained entry per ENABLED field — enablement IS presence here. ' +
            'Each carries where it sits and its full display configuration; a renderer ' +
            'filters by region, sorts by order, and reads configuration — one pass, no ' +
            'joins. fieldIds are unique (readers keep the first occurrence).',
          type: 'array',
          items: { $ref: '#/$defs/cardComponent' },
        },
        references: {
          description:
            'Applications currently using this card. A card with references must not be ' +
            'deleted; a server is expected to re-enforce that rather than trust a client.',
          type: 'array',
          items: { enum: APPLICATION_IDS },
          uniqueItems: true,
        },
        updatedAt: { type: 'string', format: 'date-time' },
      },
    },

    cardComponent: {
      type: 'object',
      required: ['fieldId', 'configuration'],
      properties: {
        fieldId: { $ref: '#/$defs/fieldId' },
        configuration: { $ref: '#/$defs/fieldConfiguration' },
        region: {
          description:
            'Where the block renders. Absent for a field that contributes no block of ' +
            'its own (the unit of measure only suffixes prices). The primary image ' +
            'always says "photo" — it IS that region\'s background.',
          enum: REGION_IDS,
        },
        order: {
          description:
            'Position within the region — and within the photo anchor, which is its own ' +
            'sequence. Contiguous from 0.',
          type: 'integer',
          minimum: 0,
        },
        rowKey: {
          description:
            'Two Info components sharing a rowKey render side by side in one row. A key ' +
            'that pairs nothing is dropped, so a rowKey present here always has a partner.',
          type: 'string',
          maxLength: MAX_ROW_KEY_LENGTH,
        },
        anchor: {
          description:
            'The slot within the region. Photo regions use one of the six overlay slots; ' +
            'the floating region exposes exactly one, "sticky". Absent for Info.',
          enum: ANCHORS,
        },
      },
    },

    fieldConfiguration: {
      type: 'object',
      required: ['displayLabel', 'alignment'],
      properties: {
        displayLabel: {
          description: 'Rendered before the value. Empty means no label at all.',
          type: 'string',
          maxLength: MAX_DISPLAY_LABEL_LENGTH,
        },
        alignment: { enum: ['left', 'center', 'right'] },
        imageFit: {
          description: 'Primary image only.',
          enum: [...IMAGE_FITS],
          default: DEFAULT_IMAGE_FIT,
        },
        customCss: {
          description:
            'The element\'s own CSS declarations AS THE AUTHOR TYPED THEM — never ' +
            'sanitised on write. A renderer MUST confine this before use: parse the ' +
            'declarations, drop anything that is not a property/value pair, and scope the ' +
            'result to the element. Treating it as trusted CSS is a vulnerability.',
          type: 'string',
          maxLength: MAX_CUSTOM_CSS_LENGTH,
        },
        descStyle: {
          description: 'Short description only. "partly" clamps to two lines.',
          enum: [...DESCRIPTION_STYLES],
          default: DEFAULT_DESCRIPTION_STYLE,
        },
        strikethrough: {
          description:
            'Price fields only. When true the value is rendered with a line through ' +
            'it. Absent means false.',
          type: 'boolean',
          default: false,
        },
        showUom: {
          description:
            'Price fields only. When true the unit of measure is appended to this ' +
            'value. Per element: one price may carry the unit while another does not. ' +
            'Absent means false.',
          type: 'boolean',
          default: false,
        },
        cta: {
          $ref: '#/$defs/ctaConfiguration',
          default: { ...DEFAULT_CTA_CONFIGURATION },
        },
      },
    },

    ctaConfiguration: {
      type: 'object',
      required: ['label', 'showText', 'showIcon', 'fullWidth', 'widthPct', 'heightPx'],
      properties: {
        label: {
          description: "Empty falls back to the product's own CTA label at render time.",
          type: 'string',
          maxLength: MAX_CTA_LABEL_LENGTH,
        },
        showText: { type: 'boolean' },
        showIcon: { type: 'boolean' },
        fullWidth: { type: 'boolean' },
        widthPct: {
          description: "Percentage of the card's content width. Ignored while fullWidth.",
          type: 'integer',
          minimum: CTA_WIDTH_PCT_RANGE.min,
          maximum: CTA_WIDTH_PCT_RANGE.max,
          multipleOf: CTA_WIDTH_PCT_RANGE.step,
        },
        heightPx: {
          type: 'integer',
          minimum: CTA_HEIGHT_PX_RANGE.min,
          maximum: CTA_HEIGHT_PX_RANGE.max,
          multipleOf: CTA_HEIGHT_PX_RANGE.step,
        },
      },
    },
  },
});

/** The schema as one frozen object, for callers that just want to validate. */
export const PRODUCT_CARD_SCHEMA = buildProductCardSchema();
